import { Component, DestroyRef, Injector, OnDestroy, OnInit, Signal, computed, effect, inject, input, output, signal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { BmtBillingAsync } from 'src/app/store/billing/async';
import { IBmtBillingState } from 'src/app/store/billing/state';
import { matchAccountOrRoutingNumber } from 'src/app/utils/validations/field-rules';
import moment from 'moment';
import { getAchError, isFetching } from 'src/app/store/billing/selector';
import { startWith } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { BillingClientSelection, BillingSubGroupSelection } from '../billing.component';

type AchPaymentRegistrationDetails = {
  payer: string;
  groupSpecifiedIdentifier: string;
  groupName: string;
  subgroupSpecifiedIdentifiers: string[];
  effectiveDate: string;
  phoneNumber: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  bankAccountDetails: {
    accountType: string;
    nameOnAccount: string;
    bankName: string;
    accountNumber: string;
    routingNumber: string;
    address: string;
  };
};

type AchPaymentRequest = {
  plan: string;
  asOfDate: string;
  groupSubGroups: {
    group: string;
    subgroups: string[];
  };
  bankAccountDetails: {
    accountType: string;
    accountName: string;
    bankName: string;
    accountNumber: string;
    routingNumber: string;
    address: {
      line1: string;
      line2: string;
      city: string;
      state: string;
      country: string;
      zip: string;
    };
  };
  requesterInfo: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    emailAddress: string;
    effectiveDate: string;
  };
};

@Component({
    selector: 'ach-payment',
    templateUrl: 'ach-payment.component.html',
    styleUrls: ['ach-payment.component.scss'],
    standalone: false
})
export class AchPaymentComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  luxId = 'BMT-ACHPayment-Comp' + this.constructor.name;
  readonly groupDetails = input<BillingClientSelection | BillingSubGroupSelection[] | null>(null);
  readonly isAllSubclientSelected = input(false);
  readonly resetSelected = output<boolean>();
  phoneMask = { mask: '(000) 000-0000' };
  readonly achRegistrationForm: UntypedFormGroup;
  readonly accountType = signal('');
  readonly dropDownCountries = signal([{
    label: '',
    value: ''
  }]);
  readonly dropDownStates = signal([{
    label: '',
    value: ''
  }]);
  private readonly achErrorStore = toSignal(this.lux.get(getAchError, this.luxId), { initialValue: '' });
  readonly isFetching = toSignal(this.lux.get(isFetching, this.luxId), { initialValue: false as boolean | string });
  readonly dismissAchError = signal(false);
  readonly achError = computed(() => this.dismissAchError() ? '' : this.achErrorStore());
  readonly acknowledge = signal(true);
  readonly achPaymentRegistrationDetails = signal<AchPaymentRegistrationDetails | null>(null);
  readonly showConfirmation = signal(false);
  readonly isRegistrationSuccess = signal(false);
  readonly payer = signal('');
  readonly achPaymentRequest = signal<AchPaymentRequest | null>(null);
  readonly disableStates = signal(false);
  readonly isACHRegistrationSuccess = signal(false);
  private readonly countryValue: Signal<string>;

  constructor(private readonly fb: UntypedFormBuilder, private readonly BBAsync: BmtBillingAsync,
              private readonly lux: Lux<{ billingState: IBmtBillingState }>) {
    this.achRegistrationForm = this.createAchRegistrationForm();
    this.countryValue = toSignal(this.achRegistrationForm.controls.country.valueChanges.pipe(
      startWith(this.achRegistrationForm.controls.country.value)
    ), {
      initialValue: this.achRegistrationForm.controls.country.value,
      injector: this.injector
    });

    effect(() => {
      const country = this.countryValue() || '';
      this.disableStates.set(!!country && country !== 'US');
    });
  }

  ngOnInit(): void {
    this.setCountriesDropDown();
    this.setStatesDropDown('US');
  }

  setStatesDropDown(country: string): void {
    this.BBAsync.getStates(country)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(states => {
        if (states) {
          const tempAry = (states as {
            value: string;
            code: string
          }[]).map(res => {
            return {
              label: res.value,
              value: res.code
            };
          });
          this.dropDownStates.set([{ label: '', value: '' }, ...tempAry]);
        }
      });
  }

  setCountriesDropDown(): void {
    this.BBAsync.getCountries()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(countries => {
        if (countries) {
          const tempAry = (countries as {
            value: string;
            code: string
          }[]).map(res => {
            return {
              label: res.value,
              value: res.code
            };
          });
          this.dropDownCountries.set([{ label: '', value: '' }, ...tempAry]);
        }
      });
  }

  private createAchRegistrationForm(): UntypedFormGroup {
    return this.fb.group({
      firstName: ['', [Validators.required, Validators.pattern('^[a-zA-Z.\'-]+( +[a-zA-Z.\'-]+)*$'), Validators.maxLength(50)]],
      lastName: ['', [Validators.required, Validators.pattern('^[a-zA-Z.\'-]+( +[a-zA-Z.\'-]+)*$'), Validators.maxLength(50)]],
      bankName: ['', [Validators.pattern('^[0-9a-zA-Z- ]*$'), Validators.required, Validators.maxLength(50)]],
      line1: ['', [Validators.required, Validators.maxLength(50)]],
      line2: [''],
      city: ['', [Validators.required]],
      state: ['', [Validators.required]],
      country: ['US', [Validators.required]],
      zip: ['', [Validators.required, Validators.pattern('^[0-9]{5}?$')]],
      accountName: ['', [Validators.required, Validators.pattern('^[a-zA-Z.\'-]+( +[a-zA-Z.\'-]+)*$'), Validators.maxLength(50)]],
      accountNumber: ['', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.minLength(5), matchAccountOrRoutingNumber('accountNumber', 'confirmAccountNumber')]],
      confirmAccountNumber: ['', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.minLength(5), matchAccountOrRoutingNumber('accountNumber', 'confirmAccountNumber')]],
      routingNumber: ['', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.minLength(9), matchAccountOrRoutingNumber('routingNumber', 'confirmRoutingNumber')]],
      confirmRoutingNumber: ['', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.minLength(9), matchAccountOrRoutingNumber('routingNumber', 'confirmRoutingNumber')]],
      effectiveDate: ['', Validators.required],
      phoneNumber: ['', [Validators.required]],
      emailAddress: ['', [Validators.required, Validators.maxLength(100), Validators.pattern('^[A-Z0-9a-z._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$')]],
    });
  }

  achPaymentsDetails() {
    const achFormValues = this.achRegistrationForm.value;
    const address = achFormValues.line1 + ',' + achFormValues.line2 + ',' + achFormValues.city + ',' + achFormValues.state + ',' + achFormValues.country + ',' + achFormValues.zip;
    const groupDetails = this.groupDetails();
    const isAllSubclientSelected = this.isAllSubclientSelected();
    const resolvedGroupDetails = this.resolveGroupDetails(groupDetails, isAllSubclientSelected);
    if (!resolvedGroupDetails) {
      return;
    }
    this.payer.set(resolvedGroupDetails.payer);
    // Delete below request object and related state and api calls once peoplesoft team implements ach get call
    this.achPaymentRegistrationDetails.set({
      payer: this.payer(),
      groupSpecifiedIdentifier: resolvedGroupDetails.groupSpecifiedIdentifier,
      groupName: resolvedGroupDetails.groupName,
      subgroupSpecifiedIdentifiers: resolvedGroupDetails.subgroupSpecifiedIdentifiers,
      effectiveDate: moment(achFormValues.effectiveDate).format('YYYY-MM-DD'),
      phoneNumber: this.formatPhoneNumber(achFormValues.phoneNumber),
      emailAddress: achFormValues.emailAddress,
      firstName: achFormValues.firstName,
      lastName: achFormValues.lastName,
      bankAccountDetails: {
        accountType: this.accountType(),
        nameOnAccount: achFormValues.accountName,
        bankName: achFormValues.bankName,
        accountNumber: achFormValues.accountNumber,
        routingNumber: achFormValues.routingNumber,
        address: address
      }
    });
    this.achPaymentRequest.set({
      plan: resolvedGroupDetails.payer,
      asOfDate: moment(achFormValues.effectiveDate).format('YYYY-MM-DD'),
      groupSubGroups: {
        group: resolvedGroupDetails.groupSpecifiedIdentifier,
        subgroups: resolvedGroupDetails.subgroupSpecifiedIdentifiers
      },
      bankAccountDetails: {
        accountType: this.accountType(),
        accountName: achFormValues.accountName,
        bankName: achFormValues.bankName,
        accountNumber: achFormValues.accountNumber,
        routingNumber: achFormValues.routingNumber,
        address: {
          line1: achFormValues.line1,
          line2: achFormValues.line2,
          city: achFormValues.city,
          state: achFormValues.state,
          country: achFormValues.country,
          zip: achFormValues.zip
        }
      },
      requesterInfo: {
        firstName: achFormValues.firstName,
        lastName: achFormValues.lastName,
        phoneNumber: achFormValues.phoneNumber,
        emailAddress: achFormValues.emailAddress,
        effectiveDate: moment(achFormValues.effectiveDate).format('YYYY-MM-DD'),
      }
    });
    this.showConfirmation.set(true);
    this.dismissAchError.set(true);
  }

  private resolveGroupDetails(
    groupDetails: BillingClientSelection | BillingSubGroupSelection[] | null,
    isAllSubclientSelected: boolean
  ): {
    payer: string;
    groupSpecifiedIdentifier: string;
    groupName: string;
    subgroupSpecifiedIdentifiers: string[];
  } | null {
    if (!groupDetails) {
      return null;
    }

    if (isAllSubclientSelected) {
      if (Array.isArray(groupDetails)) {
        return null;
      }

      return {
        payer: groupDetails.planAcronym,
        groupSpecifiedIdentifier: groupDetails.specifiedId,
        groupName: groupDetails.name,
        subgroupSpecifiedIdentifiers: ['all']
      };
    }

    if (!Array.isArray(groupDetails) || !groupDetails.length) {
      return null;
    }

    return {
      payer: groupDetails[0].client.planAcronym,
      groupSpecifiedIdentifier: groupDetails[0].client.specifiedId,
      groupName: groupDetails[0].client.name,
      subgroupSpecifiedIdentifiers: groupDetails.map(x => x.specifiedId)
    };
  }

  register(): void {
    const achPaymentRegistrationDetails = this.achPaymentRegistrationDetails();
    const achPaymentRequest = this.achPaymentRequest();
    if (!achPaymentRegistrationDetails || !achPaymentRequest) {
      return;
    }

    this.dismissAchError.set(false);
    forkJoin([
      this.BBAsync.registerAchPayment(achPaymentRegistrationDetails),
      this.BBAsync.registerAchPayDetails(achPaymentRequest)
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([paymentRegistrationResponse, achDetailsResponse]) => {
        if (paymentRegistrationResponse && paymentRegistrationResponse.status === 200) {
          this.isRegistrationSuccess.set(true);
        }
        if (achDetailsResponse && achDetailsResponse.status === 200) {
          this.isACHRegistrationSuccess.set(true);
        }
      });
  }

  onAccountTypeChange(event: Event): void {
    this.accountType.set((event.target as HTMLInputElement | null)?.value || '');
  }

  acknowledgeSelected(event: Event) {
    this.acknowledge.set(!(event.target as HTMLInputElement | null)?.checked);
  }

  formatPhoneNumber(phnNumber) {
    phnNumber = phnNumber.replace(/[- )(]/g, '');
    return phnNumber;
  }

  dialogClose(_event) {
    if (this.isRegistrationSuccess()) {
      this.resetSelected.emit(true);
      this.achRegistrationForm.reset();
    }
    this.dismissAchError.set(true);
    this.showConfirmation.set(false);
  }

  phoneNumberFormat(phnNumber) {
    phnNumber = phnNumber.replace(/\D/g, '').match(/(\d{3})(\d{3})(\d{4})/);
    phnNumber = '(' + phnNumber[1] + ') ' + phnNumber[2] + '-' + phnNumber[3];
    return phnNumber;
  }

  ngOnDestroy(): void {
    this.lux.destroy(this.luxId);
  }

}
